import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Sparkles, Crown, Zap, Building2 } from "lucide-react";

const getPackageIcon = (tier, customIcon) => {
  if (customIcon && customIcon !== "⭐" && customIcon !== "📦") {
    return <span className="text-4xl">{customIcon}</span>;
  }

  switch (tier) {
    case "free": return <Sparkles className="w-8 h-8 text-[#C19A6B]" />;
    case "premium": return <Crown className="w-8 h-8 text-[#C19A6B]" />;
    case "pro": return <Zap className="w-8 h-8 text-[#C19A6B]" />;
    case "business": return <Building2 className="w-8 h-8 text-[#C19A6B]" />;
    default: return <Sparkles className="w-8 h-8 text-[#C19A6B]" />;
  }
};

export default function CompactPackageCard({
  pkg,
  isRecommended = false,
  onSelect,
  buttonText = "Jetzt starten"
}) {
  // Color configuration based on tier
  const tierColors = {
    free: "from-green-100 to-green-50",
    premium: "from-yellow-100 to-yellow-50",
    pro: "from-cyan-100 to-cyan-50",
    business: "from-indigo-100 to-indigo-50"
  };

  const backgroundColor = tierColors[pkg.tier] || "from-sage-100 to-sage-50";

  // Use feature_list from backend, fallback to features (handle both array and object)
  let featureList = pkg.feature_list || [];
  if (featureList.length === 0 && pkg.features) {
    if (Array.isArray(pkg.features)) {
      featureList = pkg.features;
    } else if (typeof pkg.features === 'object') {
      featureList = Object.keys(pkg.features).filter(key => pkg.features[key] === true);
    }
  }

  // Use description_list from backend for bullet points
  const descriptionList = pkg.description_list || [];

  return (
    <Card className={`relative h-full flex flex-col bg-gradient-to-br ${backgroundColor} ${isRecommended ? 'border-accent border-2 shadow-lg' : 'border-border/50'}`}>
      {isRecommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
          <Badge variant="default" className="bg-[#001D24] text-white">
            ✨ Beliebt
          </Badge>
        </div>
      )}

      {/* Header Section - Fixed Height */}
      <CardHeader className="text-center pb-2 pt-6">
        <div className="w-14 h-14 mx-auto mb-3 bg-gray-100 rounded-2xl flex items-center justify-center">
          {getPackageIcon(pkg.tier, pkg.icon)}
        </div>
        <CardTitle className="text-xl font-bold">{pkg.name}</CardTitle>
      </CardHeader>

      {/* Description Section - Fixed Height */}
      <div className="px-6 h-20 flex items-start justify-center">
        {descriptionList.length > 0 ? (
          <div className="space-y-0.5 text-center">
            {descriptionList.slice(0, 3).map((item, idx) => (
              <p key={idx} className="text-xs text-muted-foreground leading-tight">• {item}</p>
            ))}
          </div>
        ) : pkg.description ? (
          <p className="text-xs text-muted-foreground text-center">{pkg.description}</p>
        ) : null}
      </div>

      {/* Price Section - Fixed Height */}
      <div className="px-6 py-4 text-center h-20 flex flex-col justify-center">
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-3xl font-bold text-foreground">
            {pkg.price === 0 ? 'Kostenlos' : `€${Math.round(pkg.price)}`}
          </span>
          {pkg.price > 0 && pkg.is_subscription && (
            <span className="text-sm text-muted-foreground">/Monat</span>
          )}
          {pkg.price > 0 && !pkg.is_subscription && (
            <span className="text-sm text-muted-foreground">einmalig</span>
          )}
        </div>
      </div>

      {/* Features Section - Flexible but with min-height */}
      <CardContent className="flex-1 flex flex-col pt-0">
        <div className="space-y-2 flex-1 min-h-[160px]">
          {featureList.slice(0, 6).map((feature, idx) => (
            <div key={idx} className="flex items-start gap-2 text-sm">
              <Check className="w-4 h-4 text-[#001D24] flex-shrink-0 mt-0.5" />
              <span className="leading-tight">{feature}</span>
            </div>
          ))}
          {featureList.length > 6 && (
            <p className="text-xs text-muted-foreground text-center pt-1">
              + {featureList.length - 6} weitere Features
            </p>
          )}
        </div>

        {/* CTA Button - Always at bottom */}
        <Button
          onClick={() => onSelect && onSelect(pkg.id)}
          className={`w-full rounded-full mt-4 ${isRecommended
            ? "bg-accent text-accent-foreground hover:bg-accent/90"
            : "bg-primary text-primary-foreground hover:bg-primary/90"
            }`}
        >
          {buttonText}
        </Button>
      </CardContent>
    </Card>
  );
}
